// uploadFragmentVideo — resumable Firebase Storage upload of a worker's
// raw mp4/mov. Stage 10:
//
//   1. Worker picks a video file (<200MB, client-side pre-check).
//   2. uploadBytesResumable streams it to fragments-videos-raw/{fid}.mp4.
//   3. CF (transcodeFragmentVideo) onObjectFinalize then ffmpeg-transcodes
//      to fragments-videos/{fid}.mp4 (H.264 720p faststart).
//   4. Caller polls getDownloadURL on the transcoded path until 200.
//
// We expose the resumable task so the caller (FragmentNew form) can show
// progress bar + cancel button.

import { getFirebase } from "@goodbye/firebase";
import {
  type UploadTask,
  type UploadTaskSnapshot,
  ref,
  uploadBytesResumable,
} from "firebase/storage";

const MAX_BYTES = 300 * 1024 * 1024;

export interface UploadFragmentVideoInput {
  fragmentId: string;
  file: File;
  onProgress?: (pct: number) => void;
}

export interface UploadFragmentVideoHandle {
  /** The resumable task — call .cancel() to abort, .pause() to suspend. */
  task: UploadTask;
  /** Resolves once the raw upload finishes (NOT the transcode). */
  done: Promise<{ rawStoragePath: string }>;
}

export function uploadFragmentVideo(
  input: UploadFragmentVideoInput,
): UploadFragmentVideoHandle {
  const { fragmentId, file, onProgress } = input;
  if (file.size > MAX_BYTES) {
    throw new Error(
      "影片超過 300MB、請先在手機上剪短或選短一點的影片。",
    );
  }
  const storage = getFirebase().storage;
  const rawStoragePath = `fragments-videos-raw/${fragmentId}.mp4`;
  const storageRef = ref(storage, rawStoragePath);
  const task = uploadBytesResumable(storageRef, file, {
    contentType: file.type || "video/mp4",
  });

  const done = new Promise<{ rawStoragePath: string }>((resolve, reject) => {
    task.on(
      "state_changed",
      (snap: UploadTaskSnapshot) => {
        if (onProgress && snap.totalBytes > 0) {
          onProgress(snap.bytesTransferred / snap.totalBytes);
        }
      },
      (err) => reject(err),
      () => resolve({ rawStoragePath }),
    );
  });

  return { task, done };
}
